import React, { useEffect, useMemo, useState } from "react";
import { tableProps } from "../../../../../../styles/muiProps";
import { MaterialReactTable } from "material-react-table";
import { columns2 } from "./columns";
import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { get5d } from "./api";
import { usePMS } from "../PMSContext";

// Safely extract data from revision (handles string or object)
const getRevisionData = (rev) => {
  if (!rev) return null;
  const raw = rev.data ?? rev;
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

// Get Summary array from latest revision
const getSummaryFromLatestRev = (project) => {
  const revs = project?.revisions || [];
  if (!revs.length) {
    console.log("5D: No revisions found for project", project?.id);
    return [];
  }

  // Get the latest revision (last in array)
  const latestRev = revs[revs.length - 1];
  console.log("5D: Latest revision", {
    revisionIndex: revs.length - 1,
    revisionData: latestRev,
  });

  const revData = getRevisionData(latestRev);
  if (!revData) {
    console.log("5D: Could not extract revision data");
    return [];
  }

  console.log("5D: Extracted revision data", {
    hasEstimationTable: !!revData.estimationTable,
    hasDataEstimationTable: !!revData.data?.estimationTable,
  });

  // Navigate to estimationTable[0].Summary
  const estimationTable =
    revData.estimationTable || revData.data?.estimationTable;
  if (!estimationTable) {
    console.log("5D: No estimationTable found in revision data");
    return [];
  }

  // estimationTable might be array or object with "0" key
  const est0 = Array.isArray(estimationTable)
    ? estimationTable[0]
    : estimationTable["0"] || estimationTable[0];

  if (!est0) {
    console.log("5D: estimationTable[0] not found", {
      isArray: Array.isArray(estimationTable),
      keys: !Array.isArray(estimationTable) ? Object.keys(estimationTable) : [],
    });
    return [];
  }

  const summary = est0.Summary;
  const result = Array.isArray(summary) ? summary : [];
  console.log("5D: Summary rows extracted", { count: result.length });
  return result;
};

// Map estimation Summary row to 5D table format
// NOTE: only fill estimated side; leave all actual columns empty
const mapSummaryRowTo5d = (row, project, actualData) => {
  // console.log("row maping", row);
  const qty = Number(row.procuredQty) || 0;
  const unitCost = Number(row.unitCost) || 0;
  const description =
    row.MaterialDescription ||
    row.Description ||
    row["Material Description"] ||
    "";
  const rowActualData = actualData?.find((m) => m.description === description);
  console.log("mapSummaryRowTo5d actualData", actualData, rowActualData);
  return {
    // Grouping key
    costCode: row["Cost Code"] || project?.type || "Structure",

    // Material description
    description,

    // Estimated columns (matches columns2 structure)
    quantity: qty,
    estQuantity: qty,
    estUnit: row.Unit || "",
    unit: row.Unit || "",
    estCost: unitCost * qty,
    ["Cost (US $)"]: qty * unitCost || 0,

    // actual-side fields here (procuredQty, poMaterials, etc.)
    actualQuantity: rowActualData?.actualQuantity || "",
    actualCost: rowActualData?.actualCost * rowActualData?.actualQuantity || "",
  };
};

const Project5d = ({ plant = false, projectFilter = [] }) => {
  const { plantId } = useParams();
  const { plantProjects, selectedProjectIndices } = usePMS();
  const [data, setData] = useState([]);
  const [actualData, setActualData] = useState([]);

  // Determine which plant projects to use (no refetch, just use cached data)
  const effectiveProjectIndices = useMemo(() => {
    if (!plant || plantProjects.length === 0) return [];

    // Use projectFilter if provided, otherwise use selectedProjectIndices from context
    const indices =
      Array.isArray(projectFilter) && projectFilter.length > 0
        ? projectFilter
        : selectedProjectIndices;

    // If no specific selection, use all projects
    if (!Array.isArray(indices) || indices.length === 0) {
      return plantProjects.map((_, i) => i);
    }

    return indices;
  }, [plant, plantProjects, projectFilter, selectedProjectIndices]);

  useEffect(() => {
    const getActual = async () => {
      console.log("Full API responseeeeeeeeee:");
      const res = await get5d(plantId);
      console.log("Full API responseeeeeeeeee:", res.materials);
      const actualDataMapping = res?.materials?.map((m) => {
        const description = m?.description;
        const actualQuantity = m?.procuredQty;
        const actualCost = m?.poMaterials?.[0]?.cost;

        return {
          description,
          actualQuantity,
          actualCost,
        };
      });

      res?.materials?.[0] && setActualData(actualDataMapping);
    };
    getActual();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      // Normal project mode: use existing 5D API
      if (!plant) {
        if (!plantId) {
          setData([]);
          return;
        }
        try {
          const res = await get5d(plantId);
          console.log("Full API responseeeeeeeeee:", res);
          const x = res?.materials?.map((m) => ({
            ...m,
            costCode: m?.materials?.[0]?.costCode,
          }));
          console.log("xxxxxxxxxx", x);
          setData(x || []);
        } catch (e) {
          console.error("Error loading 5D data:", e);
          setData([]);
        }
        return;
      }

      // Plant PMS mode: use latest revision from cached plantProjects
      if (!plantProjects.length || !effectiveProjectIndices.length) {
        console.log("5D Plant mode: No projects or indices", {
          plantProjectsLength: plantProjects.length,
          effectiveProjectIndicesLength: effectiveProjectIndices.length,
        });
        setData([]);
        return;
      }

      console.log("5D Plant mode: Processing projects", {
        effectiveProjectIndices,
        plantProjectsCount: plantProjects.length,
      });

      const rows = [];
      effectiveProjectIndices.forEach((idx) => {
        const proj = plantProjects[idx];
        if (!proj) {
          console.warn(`5D: Project at index ${idx} not found`);
          return;
        }

        console.log(`5D: Processing project ${idx}`, {
          projectId: proj.id,
          projectName: proj.name || proj.title,
          revisionsCount: proj.revisions?.length || 0,
        });

        const summaryRows = getSummaryFromLatestRev(proj);
        console.log(`5D: Summary rows for project ${idx}`, {
          summaryRowsCount: summaryRows.length,
          firstRow: summaryRows[0],
        });

        summaryRows.forEach((s) => {
          rows.push(mapSummaryRowTo5d(s, proj, actualData));
        });
      });

      console.log("5D: Final mapped rows", {
        rowsCount: rows.length,
        sampleRow: rows[0],
      });
      setData(rows);
    };

    loadData();
  }, [plant, plantId, plantProjects, effectiveProjectIndices, actualData]);

  console.log("final data", data);

  return (
    <Box sx={{ width: "70%" }}>
      {plant && (
        <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
          Showing latest estimation revision for selected sub projects
        </Typography>
      )}

      <MaterialReactTable
        // style={{ tableLayout: "auto" }}
        columns={columns2()}
        data={data || []}
        editDisplayMode="cell"
        // enableEditing={isEditTable}
        enableColumnActions={false}
        // enableRowActions
        positionActionsColumn="last"
        enableColumnDragging={false}
        positionToolbarAlertBanner="none"
        enableGrouping
        enableExpandAll
        initialState={{
          grouping: ["costCode"],
          expanded: false,
          pagination: { pageSize: 30 },
          columnVisibility: { Component: false },
        }}
        // enableColumnResizing={true}
        layoutMode={"grid"}
        {...tableProps}
        muiTableHeadCellProps={{
          sx: {
            borderRight: "1px solid rgba(224,224,224,1)",
            backgroundColor: "hsla(250, 86%, 97%, 1)",
            fontSize: "13px",
            pt: 2,
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            padding: "4px 8px",
            borderRight: "1px solid rgba(224,224,224,1)",
          },
        }}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            size: 5,
            muiTableHeadCellProps: {
              align: "center",
            },
            muiTableBodyCellProps: {
              align: "center",
              sx: {
                p: 0,
                "& button": {
                  ml: 0,
                },
              },
            },
          },
        }}
        // renderTopToolbarCustomActions={() => (
        //     <h4>Cost Comparison</h4>
        // )}
        defaultColumn={{
          minSize: 60, //allow columns to get smaller than default
          // maxSize: 160, //allow columns to get larger than default
          size: 80, //make columns wider by default
        }}
      />
      <Typography
        variant="subtitle2"
        sx={{ textAlign: "right", my: 1, color: "text.secondary" }}
      >
        ( <span style={{ color: "red" }}>+</span> = Overrun | 0 = On Budget |{" "}
        <span style={{ color: "green" }}>-</span> = Saving )
      </Typography>
    </Box>
  );
  // ) : (
  //   <>Loading</>
  // );
};

export default Project5d;
